import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Instagram } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { SERVICES } from "../config/services";
import { BUSINESS } from "../config/business";

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M19.321 6.733a5.78 5.78 0 0 1-3.77-1.39 5.78 5.78 0 0 1-1.97-3.343h-3.297v12.63a2.69 2.69 0 1 1-2.69-2.69c.153 0 .302.013.447.038v-3.356a6.047 6.047 0 1 0 5.54 6.008V9.24a9.027 9.027 0 0 0 5.74 2.04V7.938a5.812 5.812 0 0 1 0-1.205z" />
    </svg>
  );
}

export default function ServicesPage() {
  useSEO({
    title:
      "Decking, Pergola & Landscaping Services in Melbourne — Woodstone Construction",
    description:
      "Explore Woodstone Construction’s Melbourne services: composite & merbau decking, pergolas, artificial turf, natural turf, landscaping, and custom outdoor features.",
    canonical:
      typeof window !== "undefined"
        ? `${window.location.origin}/services`
        : undefined,
    ogImage: SERVICES[0].image,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Woodstone Construction services",
      itemListElement: SERVICES.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: s.title,
          description: s.description,
          areaServed: "Melbourne, VIC, Australia",
        },
      })),
    },
  });

  return (
    <main data-testid="services-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-8 md:pb-10">
        <div className="text-[12px] uppercase tracking-[0.25em] text-[#C9A876] font-semibold mb-4">
          Our expertise
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] tracking-tight leading-[1.05] font-medium max-w-4xl"
          style={{ fontFamily: "'Fraunces', serif" }}
        >
          Decking, Pergola &amp; Landscaping Services in Melbourne.
        </h1>
        <p className="mt-5 text-lg text-[#4A4D4A] max-w-2xl leading-relaxed">
          Built by a small team of Melbourne tradespeople — clean details,
          honest pricing, and a finish you&#39;ll actually want to show off.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12 md:pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {SERVICES.map((s, i) => (
            <article
              key={s.key}
              className="group bg-white border border-[#DCD7CE] rounded-3xl overflow-hidden shadow-[0_8px_30px_rgba(20,40,31,0.04)] hover:shadow-[0_20px_40px_rgba(20,40,31,0.09)] hover:-translate-y-1 transition-all duration-300 flex flex-col"
              data-testid={`service-card-${s.key}`}
            >
              <div className="relative overflow-hidden h-56">
                <img
                  src={s.image}
                  alt={s.alt}
                  loading={i < 3 ? "eager" : "lazy"}
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[#7A2D2F] font-semibold">
                  {s.tag}
                </div>
              </div>
              <div className="p-6 md:p-7 flex flex-col flex-1">
                <h2
                  className="text-2xl text-[#1A1A1A] tracking-tight mb-2"
                  style={{ fontFamily: "'Fraunces', serif" }}
                >
                  {s.title}
                </h2>
                <p className="text-[#4A4D4A] text-[15px] leading-relaxed">
                  {s.description}
                </p>
                <Link
                  to={`/contact?service=${encodeURIComponent(s.value)}`}
                  className="mt-6 inline-flex items-center gap-2 text-[#7A2D2F] font-semibold text-sm group/link"
                  data-testid={`service-cta-${s.key}`}
                >
                  Request a quote
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
                  />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ============= Past projects / social proof ============= */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div
          className="relative overflow-hidden rounded-3xl bg-[#0D0D0E] text-white p-8 md:p-14"
          data-testid="past-projects-callout"
        >
          <div
            aria-hidden
            className="absolute -top-16 -left-16 h-64 w-64 rounded-full bg-[#7A2D2F] blur-3xl opacity-50"
          />
          <div className="relative grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-3">
              <div className="text-[12px] uppercase tracking-[0.25em] text-[#C9A876] font-semibold mb-3">
                See our past work
              </div>
              <h2
                className="text-3xl md:text-4xl leading-tight tracking-tight"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Want to see projects we’ve built?
              </h2>
              <p className="mt-4 text-[#BDBDB4] max-w-xl">
                Every deck, pergola and landscape we finish gets posted on our
                Instagram and TikTok — the best place to see our style,
                finish, and the details that set us apart.
              </p>
            </div>

            <div className="lg:col-span-2 flex flex-col sm:flex-row lg:flex-col gap-3">
              <a
                href={BUSINESS.socials.instagram}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-2xl px-5 py-4 transition-all"
                data-testid="past-projects-instagram"
              >
                <span className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-xl bg-[#7A2D2F] flex items-center justify-center">
                    <Instagram size={18} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-[#BDBDB4]">
                      Instagram
                    </span>
                    <span className="font-semibold">@woodstone.construction</span>
                  </span>
                </span>
                <ArrowRight size={16} className="text-[#C9A876]" />
              </a>
              <a
                href={BUSINESS.socials.tiktok}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-2xl px-5 py-4 transition-all"
                data-testid="past-projects-tiktok"
              >
                <span className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-xl bg-[#7A2D2F] flex items-center justify-center">
                    <TikTokIcon width={16} height={16} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-[#BDBDB4]">
                      TikTok
                    </span>
                    <span className="font-semibold">@woodstone.construction</span>
                  </span>
                </span>
                <ArrowRight size={16} className="text-[#C9A876]" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
